import React from 'react';

import { Box, Center, Divider, Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { REVIEW_FORM_KEYS } from 'reducers/reviewFormSlice';
import { REVIEW_RESULT_VIEW, useCurrentProvider, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { useRatingResultData } from 'screens/performanceReview/ResultPage/components/ResultSpiderChart/hooks/dataHooks';
import useReviewFormRatingData from 'screens/performanceReview/hooks/reviewFormRatingHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { ViewStar } from 'assets/assetsV1/iconComponents';

import { REVIEW_QUESTION_TYPE_MAP_3 } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { Paper } from 'uiToolkitV1/Paper';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyLarge, TextBodyMedium, TextHeader1, TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

const ScoreComp = ({ rating, label, size = 'regular' }) => (
  <Flex
    flexDir={isMobileView() ? 'row' : 'column'}
    mr={isMobileView() ? '0' : '3'}
    className="score-comp"
    alignItems={isMobileView() ? 'center' : undefined}
    gap={isMobileView() ? '8px' : '0'}
  >
    <TextLabel mb={isMobileView() ? '0' : '8px'} bold color="text.g2">
      {label}
    </TextLabel>
    <Flex w={isMobileView() ? 'auto' : '10rem'} alignItems="center" gap={1} py={size === 'regular' ? '2px' : '4px'}>
      <ViewStar color="text.g2" />
      <TextBodyMedium color="text.g2" bold>
        {rating}
      </TextBodyMedium>
    </Flex>
  </Flex>
);

const SmallTextScore = ({ label, value = 'NA' }) => <ScoreComp rating={value} label={label} />;

const LargeTextScore = ({ label, value = '-' }) => <ScoreComp rating={value} label={label} size="large" />;

const REVIEW_FORM_TYPES = {
  [REVIEW_QUESTION_TYPE_MAP_3.PERFORMANCE]: {
    id: REVIEW_FORM_KEYS.GOAL,
    component: SmallTextScore,
    label: 'Goals Score',
  },
  [REVIEW_QUESTION_TYPE_MAP_3.POTENTIAL]: {
    id: REVIEW_FORM_KEYS.COMPETENCY,
    component: SmallTextScore,
    label: 'Competencies Score',
  },
  [REVIEW_QUESTION_TYPE_MAP_3.QNA]: {
    id: REVIEW_FORM_KEYS.QNA,
    component: SmallTextScore,
    label: 'Q&A Score',
  },
  [REVIEW_QUESTION_TYPE_MAP_3.OVERALL]: {
    id: REVIEW_FORM_KEYS.OVERALL,
    component: LargeTextScore,
    label: 'Overall Score',
  },
  [REVIEW_QUESTION_TYPE_MAP_3.OUTCOME]: {
    id: REVIEW_FORM_KEYS.OUTCOME,
    component: LargeTextScore,
    label: 'Outcome',
  },
};

// eslint-disable-next-line default-param-last
const find = (ratingSplit = [], typeToFind) => ratingSplit.find(({ type }) => type === typeToFind);

export const orderedPerformanceScore = (ratingSplit = []) => {
  let newOrderedRatingSplit = [];
  const sectionKeys = ratingSplit.map(({ type }) => type);

  if (sectionKeys.includes('OVERALL')) {
    newOrderedRatingSplit = [...newOrderedRatingSplit, find(ratingSplit, 'OVERALL')];
  }
  if (sectionKeys.includes('OUTCOME')) {
    newOrderedRatingSplit = [...newOrderedRatingSplit, find(ratingSplit, 'OUTCOME')];
  }
  if (sectionKeys.includes('PERFORMANCE')) {
    newOrderedRatingSplit = [...newOrderedRatingSplit, find(ratingSplit, 'PERFORMANCE')];
  }
  if (sectionKeys.includes('POTENTIAL')) {
    newOrderedRatingSplit = [...newOrderedRatingSplit, find(ratingSplit, 'POTENTIAL')];
  }
  if (sectionKeys.includes('QNA')) {
    newOrderedRatingSplit = [...newOrderedRatingSplit, find(ratingSplit, 'QNA')];
  }

  return newOrderedRatingSplit;
};

const OverallScoreComp = ({ overallScore }) => (
  <Flex direction="column" gap="8px" flex={1} alignItems="center" justifyContent="center">
    <TextBodyLarge bold color="text.g2">
      Overall performance score is
    </TextBodyLarge>
    <Flex alignItems="center" gap="5px">
      <Flex borderRadius="50%" h="40px" w="40px" bg="#E9F8FC" alignItems="center" justifyContent="center">
        <ViewStar color="#0D819F" boxSize="18px" />
      </Flex>
      <TextHeader1 color="text.g1" bold>
        {overallScore?.value}
      </TextHeader1>
    </Flex>
  </Flex>
);

const OutcomeScoreComp = ({ outcomeScore, isRatingCalibrated = true }) => (
  <Flex direction="column" gap="4px" flex={1} alignItems="center" justifyContent="center">
    <TextBodyLarge bold color="text.g2">
      Outcome
    </TextBodyLarge>
    <TextHeader1 color="text.g2" bold>
      {outcomeScore?.value}
    </TextHeader1>
    {isRatingCalibrated && (
      <Flex alignItems="center">
        <TextLabel color="text.g2" mr={0.5}>
          This score has been
        </TextLabel>
        <TextLabel bold color="text.g2">
          Calibrated
        </TextLabel>
        <TooltipInfoV1 title="To view more details please check the calibration history." />
      </Flex>
    )}
  </Flex>
);

const CalibratedInfo = () => (
  <Flex alignItems="center" mt={1}>
    <TextLabel color="text.g2" mr={1}>
      This score has been
    </TextLabel>
    <TextLabel bold color="text.g2" mr={0.5}>
      Calibrated
    </TextLabel>
    <TooltipInfoV1 title="To view more details please check the calibration history." />
  </Flex>
);

const OutcomeScoreCompSmall = ({ outcomeScore }) => (
  <Flex direction="column" alignItems="center" justifyContent="center">
    <Flex mt={1}>
      <TextLabel color="text.g2" mr={1}>
        Outcome
      </TextLabel>
      <TextLabel color="text.g2" bold>
        {outcomeScore?.value}
      </TextLabel>
    </Flex>
  </Flex>
);

const ScoreRow = ({ ratingSplit, isRatingCalibrated = false }) => {
  const ratingSplitOverallInFirstPos = orderedPerformanceScore(ratingSplit);

  const overallScore = find(ratingSplit, 'OVERALL');
  const outcomeScore = find(ratingSplit, 'OUTCOME');
  const hasOnlyOutcomeScore = outcomeScore && !overallScore;
  const { moduleName } = useOrganisationSettings();

  const formFields = ratingSplitOverallInFirstPos.filter(({ type }) => !['OVERALL', 'OUTCOME'].includes(type));

  if (isMobileView()) {
    return (
      <Flex flexDir="column" alignItems="center" w="100%" justifyContent="center">
        {overallScore && (
          <Flex w="100%" flexDirection="column" justifyContent="center" mb={4}>
            <Flex dir="column" flexDirection="column" justifyContent="center" alignItems="center">
              <OverallScoreComp overallScore={overallScore} />

              {isRatingCalibrated && <CalibratedInfo />}

              {outcomeScore && <OutcomeScoreCompSmall outcomeScore={outcomeScore} />}
            </Flex>
            <Divider mt="2px" borderColor="white" />
          </Flex>
        )}

        {hasOnlyOutcomeScore && (
          <Flex w="100%" flexDirection="column" justifyContent="center" mb={4}>
            <OutcomeScoreComp outcomeScore={outcomeScore} isRatingCalibrated={isRatingCalibrated} />
            {formFields?.length > 0 && <Divider mt="2px" borderColor="white" />}
          </Flex>
        )}

        {formFields?.length > 0 && (
          <Flex flexDir="column" w="100%">
            {formFields.map((formName, index) => {
              const { component, label } = REVIEW_FORM_TYPES[formName?.type] ?? {};
              const Component = component;
              return (
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`form-${formName}-${index}`}
                  w="100%"
                  justify="center"
                >
                  <Component
                    label={formName?.type === 'PERFORMANCE' ? `${moduleName}'s Score` : label}
                    value={formName?.value}
                  />
                </Flex>
              );
            })}
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" w="100%" justifyContent="center">
      {overallScore && (
        <Flex w="50%" justifyContent="center">
          <Flex dir="column" flexDirection="column" flex={1} justifyContent="center" alignItems="center">
            <OverallScoreComp overallScore={overallScore} />

            {isRatingCalibrated && <CalibratedInfo />}

            {outcomeScore && <OutcomeScoreCompSmall outcomeScore={outcomeScore} />}
          </Flex>
          <Center height="100px">
            <Divider orientation="vertical" borderColor="white" />
          </Center>
        </Flex>
      )}

      {hasOnlyOutcomeScore && (
        <Flex w="50%" justifyContent="center">
          <OutcomeScoreComp outcomeScore={outcomeScore} isRatingCalibrated={isRatingCalibrated} />
          {formFields?.length > 0 && (
            <Center height="100px">
              <Divider orientation="vertical" borderColor="white" />
            </Center>
          )}
        </Flex>
      )}

      {formFields?.length > 0 && (
        <Flex justifyContent="space-evenly" minWidth="50%" flexGrow={1} p={3}>
          {formFields.map((formName, index) => {
            const { component, label } = REVIEW_FORM_TYPES[formName?.type] ?? {};
            const Component = component;
            return (
              <Component
                label={formName?.type === 'PERFORMANCE' ? `${moduleName}'s Score` : label}
                // eslint-disable-next-line react/no-array-index-key
                key={`form-${formName}-${index}`}
                value={formName?.value}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

const ReviewFormFillingScore = () => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const ratingData = useReviewFormRatingData({ reviewId, employeeId, providerId });
  const { ratingSplit } = ratingData?.data ?? {};

  if (!ratingSplit || !ratingSplit?.length) {
    return null;
  }

  return (
    <Box mb={4}>
      <TextBodyMedium bold>Congrats on completing the review! Here&apos;s your breakdown.</TextBodyMedium>
      <Paper
        position="relative"
        boxShadow="0px 0px 2px rgba(0, 0, 0, 0.25)"
        w="100%"
        alignItems="center"
        overflow="hidden"
        bg="white"
        mt="24px"
        display="flex"
      >
        <ScoreRow ratingSplit={ratingSplit} />
      </Paper>
    </Box>
  );
};

const PerformanceResultScore = ({ margin = '12px 0 0 0', heading = '' }) => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const selectedView = useSelectedReviewResultView();
  const currentProvider = useCurrentProvider();
  const { data } = useRatingResultData({
    reviewId,
    employeeId,
    providerId,
    isIndividualReviewResult: selectedView === REVIEW_RESULT_VIEW.PEOPLE,
    responseId: currentProvider?.reviewId,
    providerType: currentProvider?.providerType,
  });
  const { ratingSplit, isRatingCalibrated } = data ?? {};

  if (!ratingSplit || !ratingSplit?.length) {
    return null;
  }

  return (
    <>
      {heading && (
        <Flex mt="4px">
          <TextHeader2 bold color="text.g1">
            {heading}
          </TextHeader2>
        </Flex>
      )}
      <Paper
        position="relative"
        boxShadow="0px 0px 2px rgba(0, 0, 0, 0.25)"
        w="100%"
        alignItems="center"
        overflow="hidden"
        bg="white"
        m={margin}
        display="flex"
      >
        <ScoreRow ratingSplit={ratingSplit} isRatingCalibrated={isRatingCalibrated} />
      </Paper>
    </>
  );
};

export { PerformanceResultScore, ReviewFormFillingScore };
